import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Camera, Video } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) {
      const p = user.profile || {};
      setForm({
        name: user.name || "",
        age: p.age || 40,
        location: p.location || "",
        profession: p.profession || "",
        bio: p.bio || "",
        interests: (p.interests || []).join(", "),
        relationship_goal: p.relationship_goal || "Long-term",
        photo_url: p.photo_url || "",
      });
    }
  }, [user]);

  if (!form) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const inputCls =
    "w-full rounded-xl border border-[#E7DFD5] bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A34]/30";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/profile", {
        name: form.name,
        age: parseInt(form.age, 10),
        location: form.location,
        profession: form.profession,
        bio: form.bio,
        interests: form.interests.split(",").map((s) => s.trim()).filter(Boolean),
        relationship_goal: form.relationship_goal,
      });
      await refreshUser();
      toast.success("Profile saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd);
      setForm((f) => ({ ...f, photo_url: data.photo_url }));
      await refreshUser();
      toast.success("Photo updated.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">My profile</p>
          <h1 className="font-display text-3xl sm:text-4xl text-stone-900">Tell your story</h1>
        </div>
        {user.verification_status !== "verified" && (
          <Link to="/verification" className="btn-accent text-sm" data-testid="profile-book-verification-btn">
            <Video size={16} /> Book verification
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="card-surface p-6 h-fit">
          <div className="relative w-full aspect-square rounded-2xl overflow-hidden bg-[#F3EFEA] border border-[#E7DFD5]">
            {form.photo_url ? (
              <img src={form.photo_url} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-stone-400 gap-2">
                <Camera size={28} />
                <span className="text-xs">No photo yet</span>
              </div>
            )}
            {user.verification_status === "verified" && (
              <span className="absolute top-3 left-3 badge-verified shadow-sm">
                <BadgeCheck size={12} /> Verified
              </span>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
          <button
            onClick={() => fileRef.current?.click()}
            className="btn-secondary w-full mt-4 text-sm"
            data-testid="profile-upload-photo-btn"
          >
            <Camera size={15} /> Upload photo
          </button>
          <p className="mt-3 text-xs text-stone-500 leading-relaxed">
            Tip: a clear, recent photo makes your video interview faster.
          </p>
        </div>

        <form onSubmit={save} className="md:col-span-2 card-surface p-6 space-y-4" data-testid="profile-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Name</label>
              <input value={form.name} onChange={set("name")} className={inputCls} data-testid="profile-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Age</label>
              <input type="number" min="40" max="100" value={form.age} onChange={set("age")} className={inputCls} data-testid="profile-age-input" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">City, State</label>
              <input value={form.location} onChange={set("location")} className={inputCls} data-testid="profile-location-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Profession</label>
              <input value={form.profession} onChange={set("profession")} className={inputCls} placeholder="Retired teacher, architect…" data-testid="profile-profession-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 block mb-1.5">About me</label>
            <textarea
              rows={4}
              value={form.bio}
              onChange={set("bio")}
              className={inputCls}
              placeholder="What makes you, you? What are you hoping to find?"
              data-testid="profile-bio-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Interests (comma separated)</label>
              <input value={form.interests} onChange={set("interests")} className={inputCls} placeholder="Jazz, hiking, cooking" data-testid="profile-interests-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1.5">Looking for</label>
              <select value={form.relationship_goal} onChange={set("relationship_goal")} className={inputCls} data-testid="profile-goal-select">
                <option value="Long-term">Long-term relationship</option>
                <option value="Companionship">Companionship</option>
                <option value="Travel partner">Travel partner</option>
                <option value="Friendship first">Friendship first</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary" data-testid="profile-save-btn">
            {busy ? "Saving…" : "Save profile"}
          </button>
        </form>
      </div>
    </div>
  );
}
